/* so-stock-alert — variant-level back-in-stock requests.
 *
 * Shows the form only when the SELECTED variant is inventory-TRACKED and has
 * inventory_quantity <= 0, then posts that variant's GID to so-mail. See
 * snippets/so-stock-alert.liquid for the full rationale and the server
 * contract.
 *
 * Deliberately a plain script, not a custom element: the module has no
 * template of its own to upgrade and no shadow DOM, and the PDP already pays
 * for several custom elements. It re-reads state on every variant change, so
 * it does not care when it runs relative to the rest of the page.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://links.smokeorganic.co.za/stock-alerts';
  var GID = 'gid://shopify/ProductVariant/';

  function init(root) {
    if (!root || root.dataset.soSaReady === '1') return;
    root.dataset.soSaReady = '1';

    // The section is the event target for on:variant:change, and the hidden
    // [name="id"] input inside it is the source of truth for what is
    // selected — the same contract so-preorder-pdp.js uses. Reading the
    // event's detail shape instead has broken here before.
    var section = root.closest('.shopify-section') || document;
    var dataEl = document.querySelector('[data-so-preorder-variants]');

    var variants = [];
    try {
      variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (err) {
      // Malformed data means we cannot tell stock from no-stock. Showing the
      // form anyway would invite requests for in-stock items, which the
      // server rejects — so the honest failure is to stay hidden.
      variants = [];
    }

    var dataLen = dataEl ? dataEl.textContent.length : 0;
    var form = root.querySelector('[data-so-sa-form]');
    var emailEl = root.querySelector('[data-so-sa-email]');
    var siteEl = root.querySelector('[data-so-sa-website]');
    var statusEl = root.querySelector('[data-so-sa-status]');
    var leadEl = root.querySelector('[data-so-sa-lead]');
    var errEl = root.querySelector('[data-so-sa-error]');
    var doneEl = root.querySelector('[data-so-sa-done]');
    var btn = root.querySelector('[data-so-sa-submit]');
    if (!form || !emailEl) return;

    var currentId = null;
    // Per-variant, not per-page: switching to a different sold-out flavour
    // must offer the form again rather than keep showing the previous
    // variant's confirmation.
    var submitted = {};

    function selectedId() {
      var input = section.querySelector ? section.querySelector('input[name="id"]') : null;
      return input ? String(input.value) : null;
    }

    function findIn(list, id) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(id)) return list[i];
      }
      return null;
    }

    function variantFor(id) {
      var v = findIn(variants, id);
      if (v) return v;
      // Products with more than 250 variants (the big bundles) get the rest of the list a moment
      // after load — written back into the data element and kept on window.soMoreVariantRows (quick
      // add). Re-read only when the element has grown, then merge the window rows.
      var el = document.querySelector('[data-so-preorder-variants]');
      if (el && el.textContent.length !== dataLen) {
        dataLen = el.textContent.length;
        try { variants = JSON.parse(el.textContent); } catch (err) { /* keep what we had */ }
      }
      (window.soMoreVariantRows || []).forEach(function (r) { if (r && !findIn(variants, r.id)) variants.push(r); });
      return findIn(variants, id);
    }

    function showError(msg) {
      // Unhide BEFORE writing the text. A role="alert" node that is hidden at
      // the moment its content changes is not reliably announced, and
      // unhiding text that was already there is announced even less reliably -
      // so the validation message was silent for screen-reader users.
      errEl.hidden = false;
      errEl.textContent = '';
      setTimeout(function () { errEl.textContent = msg; }, 0);
    }

    function render(id) {
      currentId = id;
      errEl.hidden = true;

      var v = variantFor(id);
      // Unknown variant: fail closed. A variant missing from the data blob is
      // a bug somewhere else, and guessing "probably out of stock" would
      // surface the form on in-stock items.
      //
      // `tracked === false` is the same fail-closed case: Shopify reports
      // inventory_quantity 0 for an UNTRACKED variant, which is always
      // buyable. Showing "Out of stock" under a live Add to cart button is
      // both wrong on the page and unanswerable afterwards - the restock scan
      // needs a 0 -> positive transition that an untracked variant never has.
      // Compared against false explicitly so a blob predating the `tracked`
      // field (undefined) keeps the old behaviour rather than hiding
      // everything.
      // Bundles: so-preorder-pdp's bundle rule decides the copy ('pre' / 'out') and hides the box when
      // every part is in stock ('in'). The request itself is for the BUNDLE variant, and so-mail only
      // accepts a variant whose own inventory_quantity is 0 or less — so a bundle whose own count still
      // reads above 0 gets no box (the request would be rejected). null = not a bundle / not known yet.
      var pdp = document.querySelector('so-preorder-pdp');
      var bState = v && pdp && pdp.isBundle && typeof pdp.bundleStateFor === 'function' ? pdp.bundleStateFor(id) : null;
      if (bState === 'in' || !v || v.tracked === false || Number(v.inventory_quantity) > 0) {
        root.hidden = true;
        return;
      }

      root.hidden = false;

      if (submitted[String(id)]) {
        form.hidden = true;
        doneEl.hidden = false;
        statusEl.hidden = true;
        leadEl.hidden = true;
        // No focus handling here: render() also runs on every variant change,
        // and moving focus from here stole it out of the flavour drawer when a
        // shopper switched back to a variant they had already submitted. The
        // success handler below moves it, once per submit.
        return;
      }

      form.hidden = false;
      doneEl.hidden = true;
      statusEl.hidden = false;
      leadEl.hidden = false;

      if (bState ? bState === 'pre' : v.inventory_policy === 'continue') {
        // Buyable right now as a pre-order, so this box is the ALTERNATIVE,
        // not a second description of the variant.
        //
        // The eyebrow names what the box is for. "Available to pre-order"
        // was the FIFTH "pre-order" on the page (flavour chip, buy button,
        // and the amber panel twice) and it labelled the email box with the
        // thing the email box is not.
        //
        // And no shipping claim here. The stock line above already
        // states "Pre-order · ships around <dates>" (F2); the old
        // copy said "we ship the moment it lands", which is a vaguer, faster-
        // sounding promise sitting in the adjacent box and undercutting the
        // specific one. That panel owns the shipping promise.
        //
        // What this copy MUST do is state the trade-off, because nothing
        // else on the page does: pre-order reserves a place in the first
        // batch, the email commits to nothing and defers the decision.
        statusEl.textContent = 'Prefer to wait?';
        // "Pre-order now", NOT "Pre-order above": the buy button's label is
        // not consistent across pre-order variants - it reads "Pre-order" on
        // some products and "Add to cart" on others (observed 2026-09-22 on
        // 313 AirsPops #14 vs the ZYN pouches). A spatial reference to a
        // button whose label may not match is worse than no reference.
        leadEl.textContent = 'Pre-order now to reserve yours from the first batch. Or we’ll email you once it’s back in stock, and you decide then.';
      } else {
        // F2: the stock line above already says "Out of stock · get notified" — say it once.
        statusEl.hidden = true;
        // Deliberately NOT "this flavour": this same string renders on
        // devices, pods and coils, where "flavour" reads as nonsense.
        leadEl.textContent = 'We’ll email you the day it’s back in stock.';
      }
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errEl.hidden = true;

      var email = (emailEl.value || '').trim();
      if (!email || email.indexOf('@') < 1 || email.indexOf('.') < 0) {
        showError('Please enter a valid email address.');
        emailEl.focus();
        return;
      }
      if (!currentId) {
        showError('Please choose a flavour first.');
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Sending…';

      // Pin the variant for the whole round trip. `currentId` is reassigned by
      // render() on every on:variant:change, and the response handlers below
      // run AFTER an await - so on a slow connection a shopper who submits on
      // sold-out Watermelon and then opens the flavour drawer and picks
      // sold-out Blueberry would see "Done" appear under BLUEBERRY, which they
      // never asked for, while Watermelon showed an empty form as though the
      // submit had failed. Two wrong impressions from one tap. The POST body
      // was always correct (evaluated synchronously); it was the painting
      // afterwards that read a moved target.
      var sentId = String(currentId);

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: email,
          // The endpoint resolves this with node(id:), so it must be a GID.
          variantId: GID + sentId,
          website: siteEl ? siteEl.value : '',
          source: 'pdp'
        })
      })
        .then(function (res) {
          if (res.ok) return null;
          if (res.status === 429) throw new Error('Too many requests just now — please try again in a minute.');
          return res.json().catch(function () { return {}; }).then(function (body) {
            if (body && body.error === 'variant not eligible') {
              throw new Error('This one looks available again — try adding it to your basket.');
            }
            throw new Error('Something went wrong. Please try again.');
          });
        })
        .then(function () {
          // Record against the variant we actually sent, always. Only repaint
          // if the shopper is still looking at it - otherwise we would stamp a
          // confirmation over whatever they switched to.
          submitted[sentId] = true;
          if (String(currentId) === sentId) {
            render(currentId);
            // Hiding the form throws focus to <body>; move it to the
            // confirmation so a keyboard user is not sent back to the top and a
            // screen reader announces it. Once per SUBMIT, for every variant -
            // a per-widget flag made every submit after the first silent
            // (review, 2026-09-23).
            try { doneEl.focus(); } catch (err) { /* focus is a nicety, never fatal */ }
          }
        })
        .catch(function (err) {
          // Same guard: an error for Watermelon must not be painted into
          // Blueberry's box, or into a hidden container where nobody sees it.
          if (String(currentId) !== sentId) return;
          showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
        })
        .then(function () {
          btn.disabled = false;
          btn.textContent = 'Notify me';
        });
    });

    // The bundle live check and the extra-variants load can answer after the variant change.
    section.addEventListener('so:bundle:state', function () { render(selectedId()); });
    document.addEventListener('so:variants:extended', function () { render(selectedId()); });
    section.addEventListener('on:variant:change', function () {
      render(selectedId());
    });

    render(selectedId());
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-so-stock-alert]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
